import { useState, useRef, useEffect } from 'react';
import { Info, X } from 'lucide-react';
import { getSectionHelp } from '../../config/produitSectionHelp.js';

/**
 * En-tête de section fiche produit — titre, sous-titre, aide ℹ️ (Lot UI-A).
 */
export default function ProduitSectionHeader({
  title,
  helpKey,
  help: helpProp,
  optional,
  /** Champ inline (label + ℹ️) — même hauteur qu'un label simple */
  variant = 'section',
  className = '',
}) {
  const help = helpProp ?? (helpKey ? getSectionHelp(helpKey) : null);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const showSubtitle =
    help?.tier === 'subtitle' || help?.tier === 'both' ? help.subtitle : null;
  const showPopoverBtn = help?.tier === 'popover' || help?.tier === 'both';

  const isField = variant === 'field';

  return (
    <div className={`${isField ? 'mb-0' : 'mb-3'} ${className}`}>
      <div
        className={`flex justify-between gap-2 ${
          isField ? 'items-center min-h-[1.75rem]' : 'items-start'
        }`}
      >
        <label
          className={`block text-gray-900 ${
            isField ? 'text-sm font-medium' : 'text-sm font-semibold'
          }`}
        >
          {title}
          {optional ? (
            <span className="ml-2 text-xs font-normal text-gray-500">(facultatif)</span>
          ) : null}
        </label>
        {showPopoverBtn && (
          <div className="relative shrink-0" ref={popRef}>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className={`rounded-md text-gray-500 hover:text-primary-700 hover:bg-primary-50 transition ${
                isField ? 'p-0.5' : 'p-1.5'
              }`}
              aria-label={`Aide : ${help.popoverTitle ?? title}`}
              aria-expanded={open}
            >
              <Info className="h-4 w-4" />
            </button>
            {open && (
              <div
                role="dialog"
                className="absolute right-0 z-30 mt-1 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg text-left"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {help.popoverTitle ?? title}
                  </p>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="p-0.5 text-gray-400 hover:text-gray-600"
                    aria-label="Fermer"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">{help.popover}</p>
              </div>
            )}
          </div>
        )}
      </div>
      {showSubtitle ? (
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">{showSubtitle}</p>
      ) : null}
    </div>
  );
}
